import React, { useEffect } from 'react';
import {
  Form,
  FormInstance,
  DateSerialized,
  RadioGroup,
  SelectBooleanInput,
  FormErrors
} from '@lightbeamhealth/blueprint-form';
import { Row, Col } from '../../../../grid';
import { StartDate } from '../date';
import { EndDate } from '../date';
import SelectMethod from '../method';
import SelectTime from '../time';
import Weekday from '../weekday';
import Timezone from '../timezone';
import { CardBlock, CardBreakout } from '../../card';
import {
  GetCurrentTZ,
  GetFormalDate,
  ShorthandDate
} from '../../../../libs/time';
import {
  doesCustomProgramUseRecheck,
  extractScheduleFlags,
  formatFlag,
  ICustomProgram
} from '../../../../libs/composer';
import Callout from '../../../../components/organisms/callout';
import { useMetaData } from '../../../../contexts/metadata';

interface GenericData {
  method: string;
  language: string;
  start: string;
  end?: string;
  tz: string;
  mode?: string;
  times: string[];
  days: number[];
  step?: number;
  _config?: Record<string, any>;
}

type GenericDataRaw = DateSerialized<GenericData, 'start' | 'end'>;

export const GenericDefaultData: GenericData = {
  method: 'message',
  language: 'en',
  start: GetFormalDate(ShorthandDate.Today),
  end: null,
  tz: GetCurrentTZ(),
  mode: 'W',
  times: ['10:00'],
  days: [1, 4],
  step: 1,
  _config: {}
};

const DATE_FORMAT = 'YYYY-MM-DD';
const { DeserializeDate, SerializeDate } = FormInstance;

interface GenericFormProps {
  setForm?: (form: FormInstance<GenericDataRaw>) => void;
  onSubmit?: (data: GenericData) => void;
  isDisabled?: boolean;
  customProgram?: ICustomProgram;
  customProgramVariants?: {
    en?: ICustomProgram;
    es?: ICustomProgram;
  } | null;
}

const GenericForm = ({
  setForm,
  onSubmit: _onSubmit,
  isDisabled,
  customProgram,
  customProgramVariants
}: GenericFormProps): JSX.Element => {
  const { terms } = useMetaData();
  const customProgramSource = customProgram?.source;
  const customProgramApp = customProgram?.app || '';
  const scheduleFlags = customProgramSource
    ? extractScheduleFlags(customProgramSource)
    : [];
  const usesRecheck = doesCustomProgramUseRecheck(customProgramSource);
  const serialize = (data: GenericDataRaw): GenericData => {
    // Ensure days are set correctly based on mode
    let days = data.days;
    if (data.mode === 'D') {
      // Daily mode: set to all 7 days
      days = [0, 1, 2, 3, 4, 5, 6];
    } else if (data.mode === 'W' && (!days || days.length === 0)) {
      // Weekly mode: ensure at least one day is selected
      days = data.days || [1];
    }
    return {
      ...data,
      start: SerializeDate(data.start, DATE_FORMAT),
      end: SerializeDate(data.end, DATE_FORMAT),
      times: data.times.slice(0, 1),
      days: days
    };
  };
  const deserialize = (data: GenericData): GenericDataRaw => {
    const config: Record<string, boolean> = {};
    scheduleFlags.map((flag) => {
      config[flag] = data._config?.[flag] || false;
      return;
    });
    // Ensure days are set correctly based on mode when deserializing
    let days = data.days;
    if (data.mode === 'D') {
      // Daily mode: set to all 7 days
      days = [0, 1, 2, 3, 4, 5, 6];
    } else if (data.mode === 'W' && (!days || days.length === 0)) {
      // Weekly mode: ensure at least one day is selected
      days = days || [1, 4];
    }
    return {
      ...data,
      start: DeserializeDate(data.start, DATE_FORMAT),
      end: DeserializeDate(data.end, DATE_FORMAT),
      days: days,
      _config: config
    };
  };
  const defaultLanguage = customProgram?.app?.includes('-es') ? 'es' : 'en';
  const defaultData = deserialize({
    ...GenericDefaultData,
    language: defaultLanguage
  });
  if (customProgramApp.includes('glp1')) {
    defaultData._config.skipToMonthly = false;
  }
  const languageOptions = [];
  if (customProgramVariants?.en != null || defaultLanguage === 'en') {
    languageOptions.push({
      label: 'English',
      value: 'en'
    });
  }
  if (customProgramVariants?.es != null || defaultLanguage === 'es') {
    languageOptions.push({
      label: 'Spanish',
      value: 'es'
    });
  }
  const showLanguageSelector = languageOptions.length > 1;
  const form = new FormInstance<GenericDataRaw, GenericData>(defaultData, {
    serialize,
    deserialize
  });
  useEffect(() => {
    if (setForm) {
      setForm(form);
    }
  }, []);
  const onChange = (data: GenericDataRaw): void => {
    // When mode changes, update days accordingly
    if (data.mode === 'D') {
      // Daily mode: set to all 7 days
      form.setFieldValue('days', [0, 1, 2, 3, 4, 5, 6]);
    } else if (data.mode === 'W') {
      // Weekly mode: if days is empty, set to default [1, 4]
      // Allow users to select all 7 days if they want
      if (!data.days || data.days.length === 0) {
        form.setFieldValue('days', [1, 4]);
      }
    }
  };
  const onSubmit = (data: GenericData): void => {
    _onSubmit(data);
  };
  const validate = (values: GenericDataRaw): FormErrors => {
    const errors: FormErrors = {};
    // Validate mode
    if (!values.mode || (values.mode !== 'D' && values.mode !== 'W')) {
      errors.mode = 'Please select either Daily or Weekly schedule mode.';
    }
    // Ensure schedule flags are correctly set
    for (const flag of scheduleFlags) {
      if (values._config[flag] == null) {
        errors[`_config[${flag}]`] = 'This field is required';
      }
    }
    // Validate days based on mode
    if (values.mode === 'W') {
      if (
        !values.days ||
        !Array.isArray(values.days) ||
        values.days.length === 0
      ) {
        errors.days =
          'At least one day of the week must be selected for weekly mode.';
      }
      // Recheck validation only applies to weekly mode
      if (usesRecheck && values.days && Array.isArray(values.days)) {
        // Check if any two selected days are consecutive (Sun=0, Mon=1, ..., Sat=6)
        const sortedDays = [...values.days].sort((a, b) => a - b);
        for (let i = 1; i < sortedDays.length; i++) {
          if (
            sortedDays[i] === sortedDays[i - 1] + 1 || // Handle consecutive days
            (sortedDays.includes(0) && sortedDays.includes(6)) // Handle when Sat and Sun are picked
          ) {
            errors.days =
              'This program uses rechecks. Days must be nonconsecutive when recheck is enabled.';
            break;
          }
        }
      }
    } else if (values.mode === 'D') {
      // For daily mode, days should be automatically set to all days
      // No validation needed as it's handled automatically
    }
    return errors;
  };
  return (
    <>
      <CardBlock bare>
        <Form
          form={form}
          onChange={onChange}
          onSubmit={onSubmit}
          disabled={isDisabled}
          validate={validate}
        >
          {(props) => {
            return (
              <>
                <CardBlock>
                  <Row>
                    <Col sm={12}>
                      <SelectMethod />
                    </Col>
                    <Col sm={12}>
                      {showLanguageSelector ? (
                        <RadioGroup
                          label="Language"
                          name="language"
                          options={languageOptions}
                          inline
                        />
                      ) : null}
                    </Col>
                  </Row>
                </CardBlock>
                <CardBlock>
                  <Row>
                    <Col sm={12}>
                      <RadioGroup
                        label="Schedule Mode"
                        name="mode"
                        options={[
                          {
                            label: 'Daily',
                            value: 'D'
                          },
                          {
                            label: 'Weekly',
                            value: 'W'
                          }
                        ]}
                        inline
                      />
                    </Col>
                  </Row>
                </CardBlock>
                <CardBlock>
                  <Row>
                    <Col sm={12}>
                      <StartDate label="Start Date" name="start" fill />
                    </Col>
                    <Col sm={12}>
                      <EndDate label="End Date" name="end" />
                    </Col>
                    <Col sm={12}>
                      <SelectTime label="Message At" name="times[0]" />
                    </Col>
                    <Col sm={12}>
                      <Timezone label="Timezone" name="tz" fill />
                    </Col>
                  </Row>
                </CardBlock>
                {props.values.mode === 'W' ? (
                  <CardBreakout>
                    <Weekday name="days" inline />
                  </CardBreakout>
                ) : null}
                {scheduleFlags.length > 0 && (
                  <CardBlock>
                    {scheduleFlags.map((flag, index) => {
                      const label = formatFlag(flag);
                      return (
                        <Row key={index}>
                          <Col sm={24}>
                            <SelectBooleanInput
                              label={label}
                              name={`_config[${flag}]`}
                              fill
                            />
                          </Col>
                        </Row>
                      );
                    })}
                  </CardBlock>
                )}
                {customProgramApp.includes('glp1') && (
                  <CardBlock>
                    <Row>
                      <Col sm={24}>
                        <SelectBooleanInput
                          label="Skip to Monthly"
                          name="_config.skipToMonthly"
                          fill
                        />
                        <Callout>
                          If yes, the {terms.patient.default} will only receive
                          the initial message and then monthly check-ins.
                        </Callout>
                      </Col>
                    </Row>
                  </CardBlock>
                )}
              </>
            );
          }}
        </Form>
      </CardBlock>
    </>
  );
};

export default GenericForm;
